import { ANSWER_ORIGIN } from './activityBatchConversion.constants';
import {
  ActivityValidationModel,
  ConversionFormModel,
  ConversionTraceModel,
  SourceExcerptModel,
  SourceExcerptRegionModel,
  UnconvertibleItemModel,
  UnconvertiblePageModel,
} from './activityBatchConversion.defaults';

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const toText = (value) => (value === null || value === undefined ? '' : String(value));

const toNumber = (value) => Number(value) || 0;

const toList = (value) => (Array.isArray(value) ? value : []);

// La base de página se conserva tal cual la emita el generador (incluido un 0): el renderer la
// compara con la del contrato para decidir si sabe interpretar el recorte, y un 1 inventado le
// haría pintar la página equivocada en silencio.
const toBase = (value) => (Number.isFinite(Number(value)) && value !== null && value !== '' ? Number(value) : SourceExcerptModel.page_number_base);

// Las coordenadas se coaccionan a número pero no se validan aquí: el renderer decide qué hacer con
// un bbox inválido y necesita distinguirlo de "sin regiones".
function sanitizeSourceExcerptRegion(region, fallbackPage) {
  return {
    ...SourceExcerptRegionModel,
    page: Number(region?.page) || fallbackPage,
    bbox: toList(region?.bbox).map(Number),
  };
}

export function sanitizeSourceExcerpt(data) {
  if (!isObject(data)) return null;

  const page = toNumber(data.page);

  return {
    ...SourceExcerptModel,
    contract_version: toText(data.contract_version),
    coordinate_space: toText(data.coordinate_space),
    page_number_base: toBase(data.page_number_base),
    page,
    regions: toList(data.regions).map((region) => sanitizeSourceExcerptRegion(region, page)),
    source_sha256: toText(data.source_sha256).trim().toLowerCase(),
    localization_method: toText(data.localization_method),
  };
}

export function sanitizeActivityValidation(data) {
  if (!isObject(data)) return null;

  return {
    ...ActivityValidationModel,
    score: toNumber(data.score),
    recommendation: toText(data.recommendation),
    feedback: toText(data.feedback),
  };
}

export function sanitizeAnswerOrigin(value) {
  const origin = toText(value).trim().toUpperCase();

  return ANSWER_ORIGIN[origin] || null;
}

export function sanitizeRetypeSuggestions(value) {
  return toList(value).filter((guid) => typeof guid === 'string' && guid.trim() !== '');
}

export function sanitizeConversionTrace(item) {
  return {
    ...ConversionTraceModel,
    template_type: toText(item?.template_type),
    validation: sanitizeActivityValidation(item?.validation),
    answer_origin: sanitizeAnswerOrigin(item?.answer_origin),
    source_excerpt: sanitizeSourceExcerpt(item?.source_excerpt),
    retype_suggestions: sanitizeRetypeSuggestions(item?.retype_suggestions),
  };
}

export function sanitizeUnconvertibleItem(item) {
  return {
    ...UnconvertibleItemModel,
    guid: toText(item?.id || item?.guid),
    inventory_item_id: toText(item?.inventory_item_id),
    position: toNumber(item?.position),
    statement: toText(item?.statement),
    reason: toText(item?.reason).trim().toUpperCase(),
    reason_detail: toText(item?.reason_detail),
    required_template: toText(item?.required_template),
    source_excerpt: sanitizeSourceExcerpt(item?.source_excerpt),
  };
}

// Contrato de GET :id/unconvertible: { id, batch_id, items, total, limit, offset }.
export function sanitizeUnconvertibleItems(payload) {
  if (!isObject(payload)) return { ...UnconvertiblePageModel, items: [] };

  const items = toList(payload.items).map(sanitizeUnconvertibleItem);

  return {
    ...UnconvertiblePageModel,
    items,
    total: Math.max(toNumber(payload.total), toNumber(payload.offset) + items.length),
    limit: toNumber(payload.limit),
    offset: toNumber(payload.offset),
  };
}

const toOption = (value) => (value?.value ? value : null);

// Valores iniciales del formulario de digitalización.
export function sanitizeConversionForm(data = {}) {
  return {
    ...ConversionFormModel,
    name: data.name ?? ConversionFormModel.name,
    pdf_file_name: data.pdf_file_name ?? ConversionFormModel.pdf_file_name,
    source_ref_guid: data.source_ref_guid ?? ConversionFormModel.source_ref_guid,
    source_url: data.source_url ?? ConversionFormModel.source_url,
    page_count: Number(data.page_count) || ConversionFormModel.page_count,
    has_metadata: data.has_metadata ?? ConversionFormModel.has_metadata,
    is_pedagogy_complete: data.is_pedagogy_complete ?? ConversionFormModel.is_pedagogy_complete,
    page_from: data.page_from ?? ConversionFormModel.page_from,
    page_to: data.page_to ?? ConversionFormModel.page_to,
    country: toOption(data.country),
    educationLevel: toOption(data.educationLevel),
    educationYear: toOption(data.educationYear),
    discipline: toOption(data.discipline),
  };
}
